/**
 * Per-field last-writer-wins merge for rundown rows (PLAN §6.1, D6).
 *
 * A row is two maps keyed by the same field paths: `f` holds the values and `h`
 * holds, per field, the HLC of the write that set it. A patch sets one or more
 * fields under ONE HLC. The whole rule:
 *
 *     for each field in the patch: apply it iff patch.hlc > h[field]
 *
 * A field the row has never seen has no `h`, so the first write always lands.
 * An equal HLC is dropped, which is what makes re-applying a patch a no-op.
 *
 * **Why this converges, and the one precondition it rests on.** For every
 * field, the surviving value is the one written under the greatest HLC among
 * all patches that set that field. "Greatest" is a total order, so the result
 * does not depend on the order patches arrive in (commutative), on how they are
 * grouped (associative), or on duplicates (idempotent). That holds as long as
 * **no two different writes share an HLC** — which the clock guarantees,
 * because the writer's node id is part of every timestamp (`hlc.ts`). Two
 * patches with the same HLC and different values for the same field would be
 * resolved by arrival order; that is a clock bug, not a merge case.
 * `lww.test.ts` proves the three properties over generated patch sets.
 *
 * **Field paths are opaque keys.** `deviceParams.vs.clip` and
 * `deviceParams.vs.in` are two independent registers, which is how concurrent
 * edits to two parameters of one piece both survive. The merge does not relate
 * a path to its prefixes: whether a schema may write `deviceParams` wholesale
 * AND `deviceParams.vs.clip` is a validation rule for ingress (PLAN §6.4), not
 * something the merge can decide.
 *
 * Pure: every function returns new maps and never mutates its inputs.
 */
import { compareHlc, parseHlc } from './hlc.js';

/** The replicated state of one row: values, and the HLC that wrote each. */
export type LwwRow<V = unknown> = {
  readonly f: Readonly<Record<string, V>>;
  readonly h: Readonly<Record<string, string>>;
};

/** The part of a row patch the merge reads (PLAN §6.1: `{rowId, set, hlc, origin, clientOpId}`). */
export type LwwPatch<V = unknown> = {
  readonly set: Readonly<Record<string, V>>;
  readonly hlc: string;
};

/** A merged row, plus which of the patch's fields actually landed. */
export type LwwMergeResult<V = unknown> = {
  readonly row: LwwRow<V>;
  /** Sorted. Empty when the patch lost on every field — the echo/audit can skip it. */
  readonly applied: readonly string[];
};

/**
 * A dotted path of identifier segments: `title`, `deviceParams.vs.clip`.
 * Deliberately strict — a path is a key in DynamoDB and SQLite JSON alike, and
 * an empty segment or a space in one is a bug upstream, not data.
 */
const FIELD_PATH = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/;

/**
 * Segment names that collide with `Object.prototype`. `__proto__` as a key
 * replaces an object's prototype instead of storing a field, and `constructor`
 * and friends are found by a plain `h[path]` lookup as inherited functions.
 * Either would make one client patch throw at every replica, and a later
 * materialiser that expands dotted paths into nested objects would turn them
 * into prototype pollution. Banned at the path level; lookups below also use
 * `Object.hasOwn` as a second line.
 */
const RESERVED_SEGMENTS = new Set([
  '__proto__', 'prototype', 'constructor', 'hasOwnProperty', 'isPrototypeOf',
  'propertyIsEnumerable', 'toLocaleString', 'toString', 'valueOf',
  '__defineGetter__', '__defineSetter__', '__lookupGetter__', '__lookupSetter__',
]);

/** Whether `path` is a well-formed field path. */
export function isValidFieldPath(path: string): boolean {
  return FIELD_PATH.test(path) && !path.split('.').some((segment) => RESERVED_SEGMENTS.has(segment));
}

/** The clock stored for `path`, reading only own properties. */
function ownClock(h: Readonly<Record<string, string>>, path: string): string | undefined {
  return Object.hasOwn(h, path) ? h[path] : undefined;
}

/** An empty row: no fields, no clocks. */
export function emptyLwwRow<V>(): LwwRow<V> {
  return { f: {}, h: {} };
}

/**
 * Apply one patch to a row.
 *
 * Throws on a malformed field path or HLC — both are rejected at ingress
 * (`@acp/api-contract`), so reaching here with one is a bug, and a merge that
 * silently skipped it would diverge replicas without a trace.
 */
export function applyLwwPatch<V>(row: LwwRow<V>, patch: LwwPatch<V>): LwwMergeResult<V> {
  // Validated up front, not only when a comparison happens: a malformed HLC on
  // a first write would otherwise be stored and poison every later comparison
  // on that field.
  parseHlc(patch.hlc);
  const f: Record<string, V> = { ...row.f };
  const h: Record<string, string> = { ...row.h };
  const applied: string[] = [];
  for (const [path, value] of Object.entries(patch.set)) {
    if (!isValidFieldPath(path)) throw new Error(`Invalid field path ${JSON.stringify(path)}`);
    const current = ownClock(h, path);
    if (current === undefined || compareHlc(patch.hlc, current) > 0) {
      f[path] = value;
      h[path] = patch.hlc;
      applied.push(path);
    }
  }
  applied.sort();
  return { row: { f, h }, applied };
}

/** Apply a sequence of patches in order. */
export function applyLwwPatches<V>(row: LwwRow<V>, patches: readonly LwwPatch<V>[]): LwwRow<V> {
  let current = row;
  for (const patch of patches) current = applyLwwPatch(current, patch).row;
  return current;
}

/**
 * Merge two replicas of the same row field by field — the whole-row form of
 * the same rule, for catch-up (`changedSince`) and the canonical `row.state`
 * echo, where a full row arrives rather than a patch.
 *
 * Commutative, associative and idempotent under the same precondition as the
 * patch merge. On an equal HLC the local value is kept: equal HLCs mean the
 * same write, so the values are the same.
 */
export function mergeLwwRows<V>(local: LwwRow<V>, remote: LwwRow<V>): LwwRow<V> {
  const f: Record<string, V> = { ...local.f };
  const h: Record<string, string> = { ...local.h };
  for (const [path, remoteHlc] of Object.entries(remote.h)) {
    parseHlc(remoteHlc);
    if (!isValidFieldPath(path)) throw new Error(`Invalid field path ${JSON.stringify(path)}`);
    const localHlc = ownClock(h, path);
    if (localHlc !== undefined && compareHlc(remoteHlc, localHlc) <= 0) continue;
    // `undefined` counts as no value: rows are JSON, which has no undefined.
    const value = Object.hasOwn(remote.f, path) ? remote.f[path] : undefined;
    if (value === undefined) {
      throw new Error(`Row has a clock for ${JSON.stringify(path)} but no value`);
    }
    f[path] = value;
    h[path] = remoteHlc;
  }
  return { f, h };
}
